<script setup>
import { computed, reactive, ref, watch } from "vue";
import { GqlService } from "@js/services/gql";
import { formatCurrency } from "@js/utils/currency";
import FileUploader from "@js/vue/components/FileUploader.vue";
import StepperBar from "@js/vue/components/StepperBar.vue";
import AddressSearch from "@js/vue/components/AddressSearch.vue";
import InfoModal from "@js/vue/components/InfoModal.vue";
import ExpeditionTile from "@js/vue/components/ExpeditionTile.vue";

const props = defineProps({
	policyId: {
		type: Number,
		required: true,
	},
	origin: Object,
	destination: Object,
	transportDate: String,
	itemTypes: {
		type: Array,
		default: () => [],
	},
	itemTitle: String,
	itemType: Number,
	itemValue: Number,
	transportCosts: Number,
	// { filename: 'invoice.pdf', data: 'base64string', mimeType: 'application/pdf' }
	existingInvoice: {
		type: Object,
		default: null,
	},
	tncUrl: String,
	tncAccepted: Boolean,
	noSpecialItems: Boolean,
	selectedPackageId: Number,
	packageIds: Array,
	packageSuggestedLabels: {
		type: Array,
		default: () => [],
	},
	currencySymbol: {
		type: String,
		required: true,
	},
	deductibleAmount: Number,
	shipmentType: {
		type: String,
		required: true,
	},
	shipmentTypes: Object,
	reference: String,
	shipName: String,
	excludedItemsWarningText: {
		type: String,
		default: "",
	},
	previousUrl: {
		type: String,
		required: true,
	},
	contactEmail: {
		type: String,
		required: true,
	},
	excludedItemsUrl: String,
	// Sanction law props
	sanctionWarningText: {
		type: String,
		default: "",
	},
	sanctionProcedureUrl: {
		type: String,
		required: true,
	},
	sanctionApproved: {
		type: Boolean,
		default: false,
	},
});

// Loading existing data
const craftToJsLocation = (location) => {
	if (!location) return null;

	return {
		latitude: location.lat,
		longitude: location.lng,
		fullAddress: location.address,
		number: location.parts.number,
		address: location.parts.address,
		city: location.parts.city,
		postcode: location.parts.postcode,
		county: location.parts.county,
		state: location.parts.state,
		country: location.parts.country,
	};
};

const createFileFromBase64 = (base64Data, filename, mimeType) => {
	if (base64Data.includes(";base64,"))
		base64Data = base64Data.split(";base64,")[1];

	const byteArray = Uint8Array.fromBase64(base64Data);
	return new File([byteArray], filename, { type: mimeType });
};

const existingFiles = ref([]);

if (props.existingInvoice) {
	const file = createFileFromBase64(
		props.existingInvoice.data,
		props.existingInvoice.filename,
		props.existingInvoice.mimeType,
	);
	existingFiles.value = [file];
}

// Form data
const formData = reactive({
	step0: {
		originData: craftToJsLocation(props.origin),
		destinationData: craftToJsLocation(props.destination),
		transportDate: props.transportDate,
		transportCosts: props.transportCosts,
	},
	step1: {
		itemTitle: props.itemTitle,
		itemType: props.itemType,
		itemValue: props.itemValue,
		purchaseInvoice: null,
		purchaseInvoiceChanged: false,
		tncAccepted: props.tncAccepted,
		noSpecialItems: props.noSpecialItems,
	},
	step2: {
		selectedPackageId: props.selectedPackageId,
	},
});
const getStepData = (step) => {
	/* prettier-ignore */
	return [
		formData.step0,
		formData.step1,
		formData.step2,
	][step] ?? null;
};

// Stepper
const stepperRef = ref(null);
const steps = ["Expeditions", "Transport items", "Package", "Finish"];
const currentStep = ref(0);

watch(currentStep, (newStep) => {
	if (newStep === 2) {
		initializeStep2();
	}
	if (newStep === 3) {
		initializeStep3();
	}
});

const fileToBase64 = (file) => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			resolve(reader.result);
		};
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
};

const handleFileChange = async (files) => {
	let newFile = null;

	if (files.length > 0) {
		newFile = {
			fileData: await fileToBase64(files[0]),
			filename: files[0].name,
		};

		if (errors.value.purchaseInvoice) {
			delete errors.value.purchaseInvoice;
		}
	}

	formData.step1.purchaseInvoice = newFile;
	formData.step1.purchaseInvoiceChanged = true;
};

// Form state
const isSubmitting = ref(false);
const errors = ref({});
const message = ref("");
const messageType = ref(""); // 'success' or 'error'

// Sanction law state
const isInSanctionList = ref(false);

const validateStep = (stepIndex) => {
	stepIndex = stepIndex ?? currentStep.value;

	errors.value = getStepErrors(stepIndex);

	return Object.keys(errors.value).length === 0;
};

const isStepValid = (stepIndex) => {
	stepIndex = stepIndex ?? currentStep.value;

	return Object.keys(getStepErrors(stepIndex)).length === 0;
};

const getSelectedItem = computed(() => {
	if (!formData.step1.itemType) return null;
	return props.itemTypes.find(
		(type) => parseInt(type.value) === parseInt(formData.step1.itemType),
	);
});

const isSelectedItemExcluded = computed(() => {
	return getSelectedItem.value?.excluded === true;
});

const isBasePremiumZero = computed(() => {
	return summaryData.value.basePremium === 0;
});

const getStepErrors = (stepIndex) => {
	stepIndex = stepIndex ?? currentStep.value;
	const newErrors = {};

	switch (stepIndex) {
		case 0:
			const step0 = formData.step0;

			if (!step0.originData) {
				newErrors.originData = "Origin address is required";
			}

			if (!step0.destinationData) {
				newErrors.destinationData = "Destination address is required";
			}

			if (!formData.step0.transportCosts) {
				newErrors.transportCosts = "Transport cost is required";
			}

			let today = new Date();
			today.setHours(0, 0, 0, 0);

			if (!step0.transportDate) {
				newErrors.transportDate = "Transport date is required";
			} else if (new Date(step0.transportDate) < today) {
				newErrors.transportDate = "Transport date must be today or later";
			}

			return newErrors;

		case 1:
			const step1 = formData.step1;

			if (!step1.itemTitle) {
				newErrors.itemTitle = "Description of goods is required";
			}

			if (!step1.itemType) {
				newErrors.itemType = "Category goods is required";
			}

			if (!step1.itemValue) {
				newErrors.itemValue = "Total value of goods is required";
			}

			if (!step1.tncAccepted) {
				newErrors.tncAccepted = "Terms and Conditions is required";
			}

			const hasExistingInvoice =
				props.existingInvoice && !step1.purchaseInvoiceChanged;
			const hasNewInvoice =
				step1.purchaseInvoiceChanged && step1.purchaseInvoice !== null;

			if (!hasExistingInvoice && !hasNewInvoice) {
				newErrors.purchaseInvoice = "Purchase invoice is required";
			}

			if (!isSelectedItemExcluded.value && !step1.noSpecialItems) {
				newErrors.noSpecialItems = "This field is required";
			}

			// NOTE: not checking total value here, as it doesn't show error messages (and would be very hacky to do so)
			// instead, the BE will throw a validation error

			return newErrors;

		case 2:
			if (!formData.step2.selectedPackageId) {
				newErrors.selectedPackageId =
					"Please select a package before proceeding to the final step";
			}

			return newErrors;

		case 3:
			return {};

		default:
			return null;
	}
};

// Info modal
const infoModalRef = ref(null);

// Step 2
let packageOptions = ref([]);
const getSuggestedPackageLabel = (packageId) =>
	props.packageSuggestedLabels.find(
		(pkg) => String(pkg.id) === String(packageId),
	)?.suggestedPackageLabel ?? null;
const applyPackageSuggestedLabels = (categories) =>
	categories.map((item) => ({
		...item,
		suggestedPackageLabel:
			getSuggestedPackageLabel(item.id) ?? item.suggestedPackageLabel ?? null,
	}));
const initializeStep2 = async () => {
	// Reset so we get the spinner rather than showing 0
	packageOptions.value = [];

	let newData = await GqlService.request(
		`
			query Categories(
				$ids: [QueryArgument]
			) {
				categories(
					group: "exInsurancePackages",
					id: $ids
				) {
					... on exInsurancePackages_Category {
						id
						title
						color
						adjective
						suggestedPackageLabel
						description
						included {
							... on includedBlock_Entry {
								option
								description { html }
							}
						}
						excludedSpecs {
							... on excludedSpecsBlock_Entry {
								option
								description { html }
							}
						}
					}
				}
			}
		`,
		{
			ids: props.packageIds,
		},
	);

	if (newData.errors) {
		message.value = newData.errors[0].message;
		messageType.value = "error";

		return;
	}
	newData = newData.data.categories;
	newData = applyPackageSuggestedLabels(newData);

	const premiumPromises = newData.map((item) =>
		GqlService.request(
			`
				query Calculation(
					$policyId: ID!,
					$packageId: ID
				) {
					calculation(
						policyId: $policyId,
						packageId: $packageId
					) {
						premium
						minimumPremium
					}
				}
			`,
			{
				policyId: props.policyId,
				packageId: item.id,
			},
		),
	);

	const premiums = await Promise.all(premiumPromises);
	newData = newData.map((item, index) => ({
		...item,
		premium: premiums[index].data.calculation.premium,
		minimumPremium: premiums[index].data.calculation.minimumPremium,
	}));

	packageOptions.value = newData;
};
const selectedPackage = computed(
	() =>
		packageOptions.value.find(
			(item) => parseInt(item.id) === formData.step2.selectedPackageId,
		) ?? null,
);

const handlePackageSelect = (packageId) => {
	formData.step2.selectedPackageId = parseInt(packageId);
	delete errors.value.selectedPackageId;
};

// Step 3
const initializeStep3 = async () => {
	GqlService.request(
		`
			query Categories(
				$ids: [QueryArgument]
			) {
				categories(
					group: "exInsurancePackages",
					id: $ids
				) {
					... on exInsurancePackages_Category {
						id
						title
						color
						adjective
						suggestedPackageLabel
						description { html }
						included {
							... on includedBlock_Entry {
								option
								description { html }
							}
						}
						excludedSpecs {
							... on excludedSpecsBlock_Entry {
								option
								description { html }
							}
						}
					}
				}
			}
		`,
		{
			ids: props.packageIds,
		},
	).then((fetchedPackages) => {
		packageOptions.value = applyPackageSuggestedLabels(
			fetchedPackages.data.categories,
		);
	});

	GqlService.request(
		`
			query Calculation(
				$policyId: ID!
			) {
				calculation(
					policyId: $policyId
				) {
					premium
					imaginaryProfit
					basePremium
					riskFactor
					riskFactorCharge
					iddComplianceFee
					certificateCost
					surchargeFactor
					surcharge
				}
			}
		`,
		{
			policyId: props.policyId,
		},
	).then((json) => {
		summaryData.value = json.data.calculation;
	});
};

// Step 4
const shipmentTypeObject = props.shipmentTypes.find(
	(type) => type.value === props.shipmentType,
);
const transportDateFormatted = new Date(props.transportDate).toLocaleDateString(
	"en-GB",
);
const summaryData = ref({});

const handleContactUs = () => {
	window.location.href = `mailto:${props.contactEmail}`;
};

// Submit form
const handleSubmit = async () => {
	// If already flagged as sanction list violation (in step 0), redirect to sanction procedure
	if (
		currentStep.value === 0 &&
		isInSanctionList.value &&
		!props.sanctionApproved
	) {
		window.location.href = props.sanctionProcedureUrl;
		return;
	}

	if (currentStep.value === 1 && isSelectedItemExcluded.value) {
		handleContactUs();
		return;
	}

	if (!validateStep()) return;

	isSubmitting.value = true;
	message.value = "";

	if (currentStep.value < 3) {
		try {
			const json = await GqlService.request(
				`
					mutation UpdatePolicy(
						$policyId: ID!,
						$originData: LocationData,
						$destinationData: LocationData,
						$transportDate: DateTime,
						$transportCosts: Float,
						$itemTitle: String,
						$itemType: ID,
						$itemValue: Float,
						$purchaseInvoice: FileInput,
						$purchaseInvoiceChanged: Boolean,
						$tncAccepted: Boolean,
						$selectedPackageId: ID,
						$noSpecialItems: Boolean
					) {
						updatePolicy(
							policyId: $policyId,
							originData: $originData,
							destinationData: $destinationData,
							transportDate: $transportDate,
							transportCosts: $transportCosts,
							itemTitle: $itemTitle,
							itemType: $itemType,
							itemValue: $itemValue,
							purchaseInvoice: $purchaseInvoice,
							purchaseInvoiceChanged: $purchaseInvoiceChanged,
							tncAccepted: $tncAccepted,
							selectedPackageId: $selectedPackageId,
							noSpecialItems: $noSpecialItems
						) {
							id
							isInSanctionList
						}
					}
				`,
				{
					policyId: props.policyId,
					...getStepData(currentStep.value),
				},
			);

			if (json.errors) {
				message.value = json.errors[0].message;
				messageType.value = "error";

				const validationErrors = json.errors[0].extensions?.validationErrors;
				if (validationErrors) errors.value = validationErrors;

				return;
			}

			// Check sanction law violation on step 0 (origin/destination)
			if (
				currentStep.value === 0 &&
				json.data.updatePolicy.isInSanctionList &&
				!props.sanctionApproved
			) {
				isInSanctionList.value = true;
				// Don't proceed to next step - show warning and let user click "Contact us"
				return;
			}

			stepperRef.value.nextStep();
		} catch (error) {
			message.value =
				error.message || "An error occurred while creating the shipper";
			messageType.value = "error";
		} finally {
			isSubmitting.value = false;
		}
	} else {
		const submitted = await GqlService.request(
			`
					mutation SubmitPolicy(
						$policyId: ID!
					) {
						submitPolicy(
							policyId: $policyId
						)
					}
				`,
			{
				policyId: props.policyId,
			},
		);

		if (submitted.errors) {
			message.value = submitted.errors[0].message;
			messageType.value = "error";
			return;
		}

		if (!submitted.data.submitPolicy) {
			message.value = "An error occurred while submitting the policy";
			messageType.value = "error";
			return;
		}

		window.location.href = props.previousUrl;
	}
};
</script>

<template>
	<!-- Stepper -->
	<stepper-bar
		v-model:current-step="currentStep"
		:steps="steps"
		:is-step-valid="isStepValid"
		ref="stepperRef"
	/>

	<!-- TODO: refactor this. It's starting to get a bit hacky, but it's the easiest way to reuse the old structure -->
	<div
		class="row justify-content-center"
		:class="{ 'form-container': currentStep !== 2 }"
	>
		<div class="col-12" :class="{ 'col-xl-9': currentStep !== 2 }">
			<!-- Success/Error Messages -->
			<div
				v-if="message"
				:class="[
					'alert',
					messageType === 'success' ? 'alert-success' : 'alert-danger',
				]"
				role="alert"
			>
				{{ message }}
			</div>

			<!-- Form -->
			<form class="row" @submit.prevent="handleSubmit">
				<!-- Step 1 -->
				<div v-if="currentStep === 0" class="step-content">
					<!-- Heading -->
					<h1 class="section-title text-dark-blue text-center font-medium">
						Expedition
					</h1>

					<div class="section-heading text-center">
						<div class="section-text text-dark-blue">
							Here are your expedition details. Please check them and edit them
							if needed.
						</div>
					</div>

					<!-- Origin -->
					<div class="col-12 mb-3">
						<label class="form-label" for="originData">
							Origin <span class="required">*</span>
						</label>

						<address-search
							v-model:address-data="formData.step0.originData"
							:is-valid="!('originData' in errors)"
							input-id="originData"
						></address-search>

						<div v-if="errors.originData" class="invalid-feedback">
							{{ errors.originData }}
						</div>
					</div>

					<!-- Destination -->
					<div class="col-12 mb-3">
						<label class="form-label" for="destinationData">
							Destination <span class="required">*</span>
						</label>

						<address-search
							v-model:address-data="formData.step0.destinationData"
							:is-valid="!('destinationData' in errors)"
							input-id="destinationData"
						></address-search>

						<div v-if="errors.destinationData" class="invalid-feedback">
							{{ errors.destinationData }}
						</div>
					</div>

					<!-- Transport date -->
					<div class="col-12 mb-3">
						<label class="form-label" for="transportDate">
							Transport date <span class="required">*</span>
						</label>

						<input
							class="form-control"
							:class="{ 'is-invalid': errors.transportDate }"
							type="date"
							v-model="formData.step0.transportDate"
							:min="new Date().toISOString().split('T')[0]"
							id="transportDate"
							required
						/>
						<div v-if="errors.transportDate" class="invalid-feedback">
							{{ errors.transportDate }}
						</div>

						<div
							v-else-if="
								formData.step0.transportDate <
								new Date().toISOString().split('T')[0]
							"
							class="invalid-feedback"
						>
							Transport date must be today or later
						</div>
					</div>

					<div class="col-12 mb-3">
						<label class="form-label" for="transportCosts">
							Transport costs <span class="required">*</span>
						</label>

						<div class="input-group">
							<input
								class="form-control"
								:class="{ 'is-invalid': errors.transportCosts }"
								type="number"
								min="0"
								v-model.number="formData.step0.transportCosts"
								step="0.01"
								id="transportCosts"
								required
							/>
							<div class="input-group-append">
								<div class="input-group-text">{{ currencySymbol }}</div>
							</div>
							<div v-if="errors.transportCosts" class="invalid-feedback">
								{{ errors.transportCosts }}
							</div>
						</div>
					</div>

					<!-- Sanction Law Warning -->
					<div
						v-if="isInSanctionList && !sanctionApproved"
						class="col-12 sanction-law-alert mb-3"
					>
						<div class="alert alert-warning align-items-center d-flex gap-3">
							<i class="ri-error-warning-line h1 mb-0"></i>
							<div
								v-if="sanctionWarningText"
								v-html="sanctionWarningText"
							></div>
							<div v-else>
								Either origin or destination country is on the sanction law
								countries list. Please contact us to proceed.
							</div>
						</div>
					</div>
				</div>

				<!-- Step 2 -->
				<div v-if="currentStep === 1" class="step-content">
					<!-- Heading -->
					<h1 class="section-title text-dark-blue text-center font-medium">
						Transport items
					</h1>

					<div class="section-heading text-center">
						<div class="section-text text-dark-blue">
							<!-- TODO: once text is available, fill it here -->
						</div>
					</div>

					<div class="col-12 mb-3">
						<label class="form-label" for="itemTitle">
							Description of goods <span class="required">*</span>
						</label>

						<input
							class="form-control"
							:class="{ 'is-invalid': errors.itemTitle }"
							type="text"
							v-model="formData.step1.itemTitle"
							id="itemTitle"
							required
						/>
						<div v-if="errors.itemTitle" class="invalid-feedback">
							{{ errors.itemTitle }}
						</div>
					</div>
					<div class="col-12 mb-3">
						<label class="form-label" for="itemType">
							Category goods <span class="required">*</span>
						</label>

						<select
							class="form-select"
							:class="{ 'is-invalid': errors.itemType }"
							v-model="formData.step1.itemType"
							id="itemType"
							required
						>
							<option value="">Select Category goods</option>
							<option
								v-for="type in props.itemTypes"
								:key="type.value"
								:value="type.value"
								:selected="type.value === props.itemType"
							>
								{{ type.label }}
							</option>
						</select>
						<div v-if="errors.itemType" class="invalid-feedback">
							{{ errors.itemType }}
						</div>
					</div>
					<div class="col-12 mb-3">
						<label class="form-label" for="itemValue">
							Total value of goods <span class="required">*</span>
						</label>
						<div class="input-group">
							<input
								class="form-control"
								:class="{ 'is-invalid': errors.itemValue }"
								type="number"
								min="0"
								v-model.number="formData.step1.itemValue"
								step="0.01"
								id="itemValue"
								required
							/>
							<div class="input-group-append">
								<div class="input-group-text">{{ currencySymbol }}</div>
							</div>
							<div v-if="errors.itemValue" class="invalid-feedback">
								{{ errors.itemValue }}
							</div>
						</div>
					</div>

					<div class="col-12 mb-3">
						<label class="form-label"
							>Purchase invoice <span class="required">*</span></label
						>
						<FileUploader
							:model-value="existingFiles"
							@update:model-value="handleFileChange"
							:error="errors.purchaseInvoice"
							label="Click to add a photo or document to continue."
							:max-files="1"
							:max-size="10 * 1024 * 1024"
						/>
					</div>
					<div
						v-if="!isSelectedItemExcluded && formData.step1.itemType"
						class="col-12 mb-3"
					>
						<div class="form-check">
							<input
								class="form-check-input"
								:class="{ 'is-invalid': errors.noSpecialItems }"
								type="checkbox"
								v-model="formData.step1.noSpecialItems"
								id="noSpecialItems"
								required
							/>
							<label class="form-label" for="noSpecialItems">
								I have filled out these details truthfully and confirm that none
								of Bolster's Insurance's
								<a href="/expeditions/excluded-items" target="_blank"
									>excluded items</a
								>
								are part of this expedition.
								<span class="required">*</span>
							</label>
							<div v-if="errors.noSpecialItems" class="invalid-feedback">
								{{ errors.noSpecialItems }}
							</div>
						</div>
					</div>
					<div class="col-12 mb-3">
						<div class="form-check">
							<input
								class="form-check-input"
								:class="{ 'is-invalid': errors.tncAccepted }"
								type="checkbox"
								v-model="formData.step1.tncAccepted"
								id="tncAccepted"
								required
							/>
							<label class="form-label" for="tncAccepted">
								I have filled out this list truthfully and accept Bolster's
								<a target="_blank" :href="tncUrl">terms and conditions</a>.
								Bolster is not responsible for underinsurance.
								<span class="required">*</span>
							</label>
							<div v-if="errors.tncAccepted" class="invalid-feedback">
								{{ errors.tncAccepted }}
							</div>
						</div>
					</div>
				</div>

				<!-- Step 3 -->
				<div v-if="currentStep === 2" class="step-content">
					<!-- Heading -->
					<h1 class="section-title text-dark-blue text-center font-medium">
						Choose your insurance package
					</h1>

					<div class="section-heading text-center">
						<div class="section-text text-dark-blue">
							Based on the information you provided we can provide the following
							insurance packages. You can choose which options fits you best.
						</div>
					</div>

					<div class="row justify-content-center mb-3">
						<template v-if="packageOptions.length > 0">
							<div
								class="col-10 col-md-6 col-xl-4 my-lg-4 my-4"
								v-for="insurancePackage in packageOptions"
								:key="insurancePackage.id"
							>
								<div
									class="package h-100"
									:class="{
										selected:
											formData.step2.selectedPackageId ===
											parseInt(insurancePackage.id),
									}"
									@click="handlePackageSelect(insurancePackage.id)"
									role="button"
								>
									<div class="package-header">
										<div class="package-selected">Selected</div>
										<div
											v-if="insurancePackage.suggestedPackageLabel"
											class="package-advice"
											v-html="insurancePackage.suggestedPackageLabel"
										></div>

										<h3 class="package-title">{{ insurancePackage.title }}</h3>

										<h6 class="package-description">Your premium:</h6>
										<h4 class="package-price">
											{{
												formatCurrency(
													insurancePackage.premium,
													props.currencySymbol,
												)
											}}
										</h4>
										<span
											v-if="
												insurancePackage.premium ===
												insurancePackage.minimumPremium
											"
											>Minimum price</span
										>
									</div>
									<div class="package-body text-start">
										<div class="package-included">
											<span class="text-blue">Included:</span>
											<ul class="package-checklist fa-ul">
												<li
													v-for="feature in insurancePackage.included"
													:key="feature"
													class="info-modal"
												>
													<span class="fa-li"
														><i class="ri-check-fill"></i
													></span>
													{{ feature.option }}
													<button
														type="button"
														class="btn btn-info"
														@click="
															infoModalRef.openInfoModal(
																feature.option,
																feature.description?.html,
															)
														"
													>
														<i class="ri-information-fill"></i>
													</button>
												</li>
											</ul>
										</div>
										<div
											v-if="
												insurancePackage.excludedSpecs &&
												insurancePackage.excludedSpecs.length > 0
											"
											class="package-included bg-white"
										>
											<span class="text-blue">Excluded:</span>
											<ul class="package-checklist fa-ul">
												<li
													v-for="excluded in insurancePackage.excludedSpecs"
													:key="excluded"
													class="info-modal"
												>
													<span class="fa-li"
														><i class="ri-close-fill"></i
													></span>
													{{ excluded.option }}
													<button
														type="button"
														class="btn btn-info"
														@click="
															infoModalRef.openInfoModal(
																excluded.option,
																excluded.description?.html,
															)
														"
													>
														<i class="ri-information-fill"></i>
													</button>
												</li>
											</ul>
										</div>
									</div>
								</div>
							</div>
						</template>
						<div v-else class="spinner-border" role="status">
							<span class="visually-hidden"></span>
						</div>
					</div>
					<div v-if="selectedPackage" class="mt-lg-5 w-75 mx-auto mb-5 mt-3">
						<h3>Selected package:</h3>
						<h2 class="text-blue">{{ selectedPackage.title }}</h2>
						<p v-html="selectedPackage.description?.html"></p>
						<h3 class="info-modal">Deductible</h3>
						All insurance packages have a standard deductible of
						{{ formatCurrency(deductibleAmount, props.currencySymbol) }}.
					</div>
					<div v-if="errors.selectedPackageId" class="invalid-feedback mb-4">
						{{ errors.selectedPackageId }}
					</div>
				</div>

				<!-- Step 4 -->
				<div v-if="currentStep === 3" class="step-content">
					<!-- Heading -->
					<h1 class="section-title text-dark-blue text-center font-medium">
						Almost done - complete your insurance
					</h1>

					<div class="section-heading text-center">
						<div class="section-text text-dark-blue">
							Please find the summary of your insurance package below. Make sure
							to double check all information! Is everything in order? Click '<b
								>Confirm & Accept</b
							>' to complete your insurance for this shipment.
						</div>
					</div>

					<h4>Expedition overview:</h4>
					<expedition-tile
						:shipmentType="shipmentTypeObject"
						:originAddress="formData.step0.originData.fullAddress"
						:destinationAddress="formData.step0.destinationData.fullAddress"
						:reference="reference ?? '-'"
						:shipName="shipName"
						:transportDate="transportDateFormatted"
					></expedition-tile>

					<template v-if="Object.keys(summaryData).length > 0">
						<div class="financial-overview my-5">
							<h5 class="mb-3">Insurance overview:</h5>

							<div class="financial-group total">
								<div class="row align-items-center g-0">
									<div class="col item item-title">
										<strong>Value of goods</strong>
									</div>
									<div class="item item-price col-auto text-end">
										{{
											formatCurrency(
												formData.step1.itemValue,
												props.currencySymbol,
											)
										}}
									</div>
								</div>
							</div>

							<div class="financial-group total">
								<div class="row align-items-center g-0">
									<div class="col item item-title">
										<strong>Transport costs</strong>
									</div>
									<div class="item item-price col-auto text-end">
										{{
											formatCurrency(
												formData.step0.transportCosts,
												props.currencySymbol,
											)
										}}
									</div>
								</div>
							</div>

							<div class="financial-group total">
								<div class="row align-items-center g-0">
									<div class="col item item-title">
										<strong>Category of goods</strong>
									</div>
									<div class="item item-price col-auto text-end">
										{{ getSelectedItem.label }}
									</div>
								</div>
							</div>

							<div class="financial-group total">
								<div class="row align-items-center g-0">
									<div class="col item item-title">
										<strong>Selected insurance package</strong>
									</div>
									<div class="item item-price col-auto text-end">
										{{ selectedPackage.title }}
									</div>
								</div>
							</div>
						</div>

						<div class="financial-overview my-5">
							<h5 class="mb-3">Premium summary:</h5>

							<div class="financial-group total">
								<div class="row align-items-center g-0">
									<div class="col item item-title">
										<strong>Base premium</strong>
									</div>
									<div class="item item-price col-auto text-end">
										{{
											formatCurrency(
												summaryData.basePremium,
												props.currencySymbol,
											)
										}}
									</div>
								</div>
							</div>

							<div
								v-if="summaryData.riskFactor > 1"
								class="financial-group total"
							>
								<div class="row align-items-center g-0">
									<div class="col item item-title">
										<strong>Additional charge for high-risk items</strong>
									</div>
									<div class="item item-price col-auto text-end">
										{{
											formatCurrency(
												summaryData.riskFactorCharge,
												props.currencySymbol,
											)
										}}
									</div>
								</div>
							</div>

							<div class="financial-group total">
								<div class="row align-items-center g-0">
									<div class="col item item-title">
										<strong>Imaginary profit</strong>
									</div>
									<div class="item item-price col-auto text-end">
										{{
											formatCurrency(
												summaryData.imaginaryProfit,
												props.currencySymbol,
											)
										}}
									</div>
								</div>
							</div>

							<div class="financial-group total">
								<div class="row align-items-center g-0">
									<div class="col item item-title">
										<strong>IDD compliance fee</strong>
									</div>
									<div class="item item-price col-auto text-end">
										{{
											formatCurrency(
												summaryData.iddComplianceFee,
												props.currencySymbol,
											)
										}}
									</div>
								</div>
							</div>

							<div class="financial-group total">
								<div class="row align-items-center g-0">
									<div class="col item item-title">
										<strong>Certificate costs</strong>
									</div>
									<div class="item item-price col-auto text-end">
										{{
											formatCurrency(
												summaryData.certificateCost,
												props.currencySymbol,
											)
										}}
									</div>
								</div>
							</div>

							<div
								v-if="summaryData.surchargeFactor > 1"
								class="financial-group total"
							>
								<div class="row align-items-center g-0">
									<div class="col item item-title">
										<strong>Surcharge</strong>
									</div>
									<div class="item item-price col-auto text-end">
										{{
											formatCurrency(
												summaryData.surcharge,
												props.currencySymbol,
											)
										}}
									</div>
								</div>
							</div>
						</div>
						<h5 class="float-start mb-5">
							Your insurance premium - '{{ selectedPackage.title }}'
						</h5>
						<h5 class="float-end mb-5">
							{{ formatCurrency(summaryData.premium, props.currencySymbol) }}
						</h5>
					</template>
					<div v-else class="row">
						<div class="spinner-border" role="status">
							<span class="visually-hidden"></span>
						</div>
					</div>
				</div>

				<!-- Alert for excluded items -->
				<div
					v-if="
						currentStep === 1 &&
						isSelectedItemExcluded &&
						props.excludedItemsWarningText
					"
					class="row mb-3"
				>
					<div class="col-12">
						<div class="alert alert-warning" role="alert">
							{{
								props.excludedItemsWarningText ?? "Excluded items are selected"
							}}
						</div>
					</div>
				</div>

				
				<!-- Alert for zero base premium -->
				<div
					v-if="
						currentStep === 3 &&
						isBasePremiumZero
					"
					class="row mb-3"
				>
					<div class="col-12">
						<div class="alert alert-danger" role="alert">
							Cannot find premium rate. Please contact administrator."
						</div>
					</div>
				</div>

				<!-- Action Buttons -->
				<div class="row justify-content-between">
					<div class="col-auto">
						<a
							:href="previousUrl"
							class="btn btn-rounded btn-outline-blue"
							v-if="previousUrl && currentStep === 0"
						>
							<i class="ri-arrow-left-line" aria-hidden="true"></i>
							<span>Back</span>
						</a>
						<button
							class="btn btn-rounded btn-outline-blue"
							v-else-if="currentStep > 0"
							@click.prevent="stepperRef.prevStep()"
						>
							<i class="ri-arrow-left-line" aria-hidden="true"></i>
							<span>Back</span>
						</button>
					</div>
					<div class="col-auto">
						<button
							v-if="currentStep === 1 && isSelectedItemExcluded"
							@click="handleContactUs"
							class="btn btn-rounded btn-blue"
							type="button"
						>
							<span>Contact us</span>
							<i class="ri-arrow-right-line" aria-hidden="true"></i>
						</button>
						<button
							v-else
							class="btn btn-rounded btn-blue"
							type="submit"
							:disabled="isSubmitting || isBasePremiumZero"
						>
							<span v-if="isSubmitting">Saving...</span>
							<span v-else-if="currentStep === 3">Confirm & Accept</span>
							<span
								v-else-if="
									currentStep === 0 && isInSanctionList && !sanctionApproved
								"
								>Contact us</span
							>
							<span v-else>Next</span>
							<i class="ri-arrow-right-line" aria-hidden="true"></i>
						</button>
					</div>
				</div>
			</form>
		</div>
	</div>

	<info-modal ref="infoModalRef"></info-modal>
</template>

<!-- replace with existing styling -->
<style scoped>
.invalid-feedback {
	display: block;
	width: 100%;
	margin-top: 0.25rem;
	font-size: 0.875em;
	color: #dc3545;
}

.btn:disabled {
	opacity: 0.65;
	cursor: not-allowed;
}

.spinner-border {
	--bs-spinner-width: 100px;
	--bs-spinner-height: 100px;
	margin: 0 auto;
}
</style>
